import React, { useState } from "react";
import { Button, toast } from "@jammable/ui-core";
import { TextArea } from "@jammable/ui-components/Forms";
import { FaChevronDown } from "react-icons/fa";
import Dropdown from "../dropdown/Dropdown";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import ReactCountryFlag from "react-country-flag";

// Define options (copied from CreateTTSFlow)
const genders = [
  { displayName: "Female", emoji: "👩", value: "female" },
  { displayName: "Male", emoji: "👨", value: "male" },
];

const accents = [
  { displayName: "American", flagCode: "US" },
  { displayName: "British", flagCode: "GB" },
  { displayName: "French", flagCode: "FR" },
  { displayName: "German", flagCode: "DE" },
  { displayName: "Spanish", flagCode: "ES" },
  { displayName: "Italian", flagCode: "IT" },
  { displayName: "Korean", flagCode: "KR" },
  { displayName: "Australian", flagCode: "AU" },
  { displayName: "Japanese", flagCode: "JP" },
];

const expressions = [
  { displayName: "Default", emoji: "😶" },
  { displayName: "Whispered", emoji: "🤫" },
  { displayName: "Happy", emoji: "😁" },
  { displayName: "Sad", emoji: "😢" },
  { displayName: "Angry", emoji: "🤬" },
  { displayName: "Shouting", emoji: "🤯" },
  { displayName: "Terrified", emoji: "😵" },
];

const TextToSpeechInputView = ({ onTextConfirmed, selectedVoices }) => {
  const [text, setText] = useState("");
  const maxChars = 2000; // Example character limit for TTS input

  // --- Determine initial gender based on selectedVoices[0] ---
  const initialGender = (() => {
    const firstVoice = selectedVoices?.[0];
    if (firstVoice?.gender) {
      const matchedGender = genders.find(
        (g) => g.value.toLowerCase() === firstVoice.gender.toLowerCase(),
      );
      if (matchedGender) {
        return matchedGender;
      }
      console.warn(
        `Voice gender '${firstVoice.gender}' not found in available options. Using default.`,
      );
    }
    // Default if no voice, no gender, or gender doesn't match
    return genders[0]; // Default to the first option (e.g., Female)
  })();
  // --- End Initial Gender Determination ---

  // Add state for TTS options
  const [gender, setGender] = useState(
    selectedVoices?.[0]?.tts?.gender === "female"
      ? { displayName: "Female", emoji: "👩", value: "female" }
      : { displayName: "Male", emoji: "👨", value: "male" },
  );
  const [accent, setAccent] = useState(accents[0]); // Default to American
  const [expression, setExpression] = useState(expressions[0]); // Default to Default

  const handleConfirmText = () => {
    if (!text.trim()) {
      toast.error("Please enter some text.");
      return;
    }
    if (text.length > maxChars) {
      toast.error(`Text exceeds the maximum limit of ${maxChars} characters.`);
      return;
    }

    // Pass the configuration back via the prop
    const ttsData = {
      text: text.trim(),
      accent: accent.flagCode,
      gender: gender.value,
      expression: expression.displayName,
    };
    onTextConfirmed(ttsData);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Back button is now rendered in the parent CreateModal's unified header */}

      <TextArea
        placeholder="Enter text to convert to speech..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        minRows={5}
        className="tw-w-full tw-bg-gray-100 "
        maxLength={maxChars + 50} // Allow slight overtyping for better UX before hard limit
      />
      <div className="tw-flex tw-justify-center">
        <div className="tw-bg-gray-100 tw-normal-nums tw-p-2 tw-text-sm tw-gap-1 tw-opacity-60 tw-py-0 tw-rounded-xl tw-font-bold tw-flex tw-items-center">
          <span
            className={`tw-normal-nums ${text.length > maxChars ? "tw-text-red-600" : "tw-opacity-50"} `}
          >
            {text.length}
          </span>
          <div className="tw-h-full tw-normal-nums tw-bg-gray-600 tw-rounded-full tw-w-[2px] tw-mx-1" />
          <span>{maxChars}</span>
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
        {/* Gender Dropdown */}
        <Dropdown
          dropdownItems={
            <>
              {genders.map((genderOption) => (
                <DropdownMenuItem
                  key={genderOption?.displayName}
                  className="tw-z-100 tw-flex tw-flex-col"
                >
                  <div
                    onClick={() => setGender(genderOption)}
                    className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-200 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                  >
                    {genderOption.emoji} {genderOption.displayName}
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          }
        >
          <div className="tw-w-full tw-cursor-pointer tw-items-center tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
            Gender
            <div className="tw-flex tw-items-center tw-gap-1.5 ">
              {gender?.emoji}
              <span className="tw-text-gray-500">{gender?.displayName}</span>
              <FaChevronDown className="tw-text-gray-400" />
            </div>
          </div>
        </Dropdown>

        {/* Accent Dropdown */}
        <Dropdown
          dropdownItems={
            <>
              {accents.map((accentOption) => (
                <DropdownMenuItem
                  key={accentOption?.displayName}
                  className="tw-z-100 tw-flex tw-flex-col tw-w-full"
                >
                  <div
                    onClick={() => setAccent(accentOption)}
                    className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-200 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                  >
                    <ReactCountryFlag
                      className="emojiFlag"
                      countryCode={accentOption.flagCode}
                      svg
                      style={{ width: "1.2em", height: "1.2em" }} // Adjust size if needed
                    />
                    {accentOption?.displayName}
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          }
        >
          <div className="tw-w-full tw-cursor-pointer tw-items-center tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
            Accent
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500">
              <ReactCountryFlag
                className="emojiFlag"
                countryCode={accent?.flagCode}
                svg
                style={{ width: "1.2em", height: "1.2em" }} // Adjust size if needed
              />
              {accent?.displayName}
              <FaChevronDown className="tw-text-gray-400" />
            </div>
          </div>
        </Dropdown>

        {/* Expression Dropdown */}
        <Dropdown
          dropdownItems={
            <>
              {expressions.map((expressionOption) => (
                <DropdownMenuItem
                  key={expressionOption.displayName}
                  className="tw-z-100 tw-flex tw-flex-col"
                >
                  <div
                    onClick={() => setExpression(expressionOption)}
                    className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-200 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                  >
                    {expressionOption.emoji} {expressionOption.displayName}
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          }
        >
          <div className="tw-w-full tw-cursor-pointer tw-items-center tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
            Expression
            <div className="tw-flex tw-items-center tw-gap-1.5 ">
              {expression?.emoji}
              <span className="tw-text-gray-500">
                {expression?.displayName}
              </span>
              <FaChevronDown className="tw-text-gray-400" />
            </div>
          </div>
        </Dropdown>
      </div>

      <Button
        onClick={handleConfirmText}
        disabled={!text.trim() || text.length > maxChars}
        className="tw-w-full tw-font-black tw-uppercase tw-text-base tw-rounded-2xl tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        Use This Text
      </Button>
    </div>
  );
};

export default TextToSpeechInputView;
